import { useQuery } from '@tanstack/react-query';
import type { CollectorPlatform, DataHealth, DeveloperHealthSummary, DeveloperRunHistory, HealthSource, SourceHealth } from '@ace-tracker/shared';
import { fetchDataHealth, fetchDeveloperHealth, fetchDeveloperRunHistory, UnauthorizedError } from './api';

export const SOURCE_LABELS: Record<HealthSource, string> = {
  copilot: 'GitHub Copilot',
  'claude-code': 'Claude Code',
  codex: 'OpenAI Codex',
  kiro: 'AWS Kiro',
  gemini: 'Gemini CLI',
  rtk: 'RTK',
  headroom: 'Headroom',
  graphify: 'Graphify',
};

export const PLATFORM_LABELS: Record<CollectorPlatform, string> = {
  windows: 'Windows',
  macos: 'macOS',
  linux: 'Linux',
  other: 'Other',
  unknown: 'Unknown',
};

/**
 * Data health for a range (coverage, freshness, collector run outcomes).
 * Shared by the header's "Data as of" line, the Executive confidence
 * indicator, the empty states, and the Data health page — one request serves
 * them all. Undefined while loading or on failure; every consumer must work
 * without it.
 */
export function useDataHealth(range: { from: string; to: string }, rangeKey: unknown, enabled = true) {
  return useQuery<DataHealth>({
    queryKey: ['dashboard-health', rangeKey],
    queryFn: () => fetchDataHealth(range),
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, err) => !(err instanceof UnauthorizedError) && failureCount < 1,
    enabled,
  });
}

const retryUnlessUnauthorized = (failureCount: number, err: unknown) => !(err instanceof UnauthorizedError) && failureCount < 1;

/** Per-developer collector health. Measured back from now, so it takes no range. Key starts with 'dashboard-health' so a Settings save refreshes it. */
export function useDeveloperHealth(enabled = true) {
  return useQuery<DeveloperHealthSummary>({
    queryKey: ['dashboard-health', 'developers'],
    queryFn: fetchDeveloperHealth,
    staleTime: 5 * 60 * 1000,
    retry: retryUnlessUnauthorized,
    enabled,
  });
}

/** One developer's collector executions in the range; fetched when their row is opened. */
export function useDeveloperRunHistory(developerId: string, range: { from: string; to: string }, enabled: boolean) {
  return useQuery<DeveloperRunHistory>({
    queryKey: ['dashboard-health', 'developer-runs', developerId, range.from, range.to],
    queryFn: () => fetchDeveloperRunHistory(developerId, range),
    staleTime: 5 * 60 * 1000,
    retry: retryUnlessUnauthorized,
    enabled,
  });
}

export function sourceHealth(health: DataHealth | undefined, source: HealthSource): SourceHealth | undefined {
  return health?.sources.find((s) => s.source === source);
}

/**
 * What an empty state is told about its source: its health, 'loading' while
 * that is still being fetched (so it does not claim a reason it does not yet
 * know), or undefined when health is unavailable.
 */
export type SourceHealthInput = SourceHealth | 'loading' | undefined;

/** Why a view has nothing to show. `unexplained`: nothing arrived and no collector run says why. */
export type EmptyStateKind = 'not-installed' | 'disabled' | 'stale' | 'zero';

export function emptyStateKind(source: SourceHealth | undefined): { kind: EmptyStateKind; unexplained: boolean } {
  switch (source?.state) {
    case 'disabled':
      return { kind: 'disabled', unexplained: false };
    case 'not-installed':
      return { kind: 'not-installed', unexplained: false };
    case 'stale':
    case 'failing':
      return { kind: 'stale', unexplained: false };
    case 'no-report':
      return { kind: 'not-installed', unexplained: true };
    default:
      // Reporting, or health not loaded: the range simply has no usage.
      return { kind: 'zero', unexplained: false };
  }
}

/** "5 min ago", "3 h ago", "2 days ago" — coarse on purpose; the exact time goes in a title attribute. */
export function timeAgo(iso: string, now: Date = new Date()): string {
  const minutes = Math.max(0, Math.round((now.getTime() - new Date(iso).getTime()) / 60_000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
}
